import type { KeyboardEvent, MouseEvent } from 'react';

import { Button } from '../Button';
import { FromPriceDisplay } from '../PriceDisplay';
import {
  ActionRow,
  Content,
  ContentRow,
  Details,
  EditSearchButton,
  Image,
  ImageAction,
  ImageBadge,
  ImageWrapper,
  ItemCardBase,
  Meta,
  OverlayContent,
  OverlayPrice,
  OverlayTitle,
  Placeholder,
  PriceRow,
  StatusBadge,
  TextColumn,
  Title,
  TitleMetaGroup,
  UnavailableOverlay,
  UnavailableOverlayDivider,
  UnavailableOverlayText,
} from './ItemCard.styles';
import { resolveCardImageSizing } from '../EntityImage';
import type { ItemCardProps } from './ItemCard.types';

export function ItemCard({
  image,
  imageAspectRatio,
  imageMobileAspectRatio,
  imageOverlay = false,
  title,
  meta,
  price,
  statusBadge,
  imageBadge,
  imageAction,
  action,
  customAction,
  unavailable,
  unavailableLabel,
  editSearchLabel,
  onEditSearch,
  ariaLabel,
  variant = 'default',
  size = 'large',
  textPosition = 'left',
  boldTitle = false,
  onSelect,
}: ItemCardProps) {
  const clickable = Boolean(onSelect);
  const isSmall = size === 'small';
  const imageSizing = resolveCardImageSizing(size, imageAspectRatio, imageMobileAspectRatio);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.();
    }
  };

  const unavailableAnnouncement = unavailable ? unavailableLabel ?? statusBadge : undefined;

  const actionButton = action && (
    <Button
      size={isSmall ? 'small' : undefined}
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        action.onClick();
      }}
    >
      {action.label}
    </Button>
  );

  const baseProps = {
    $unavailable: unavailable,
    $clickable: clickable,
    role: clickable ? 'button' : undefined,
    tabIndex: clickable ? 0 : undefined,
    'aria-label':
      ariaLabel && unavailableAnnouncement
        ? `${ariaLabel} — ${unavailableAnnouncement}`
        : ariaLabel,
    'data-cy': 'item-card',
    onClick: clickable ? onSelect : undefined,
    onKeyDown: clickable ? handleKeyDown : undefined,
  };

  const unavailableOverlay = unavailable && unavailableLabel && (
    <UnavailableOverlay>
      <UnavailableOverlayText>{unavailableLabel}</UnavailableOverlayText>
      {onEditSearch && editSearchLabel && (
        <>
          <UnavailableOverlayDivider aria-hidden="true" />
          <EditSearchButton
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              onEditSearch();
            }}
          >
            {editSearchLabel}
          </EditSearchButton>
        </>
      )}
    </UnavailableOverlay>
  );

  if (variant === 'overlay') {
    return (
      <ItemCardBase {...baseProps}>
        <ImageWrapper
          $aspectRatio={imageAspectRatio}
          $mobileAspectRatio={imageMobileAspectRatio}
          $imageOverlay={imageOverlay}
          $overlayScrim={Boolean(image.src)}
          $size={size}
        >
          {image.src ? (
            <Image src={image.src} alt={image.alt} sizing={imageSizing} />
          ) : (
            <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
          )}
          {unavailableOverlay}
          <OverlayContent>
            <OverlayTitle>{title}</OverlayTitle>
            {price && (
              <OverlayPrice>
                <FromPriceDisplay
                  amount={price.amount}
                  currencyCode={price.currencyCode}
                  label={price.label}
                  discount={price.discount}
                  locale={price.locale}
                  size="small"
                />
              </OverlayPrice>
            )}
          </OverlayContent>
        </ImageWrapper>
      </ItemCardBase>
    );
  }

  const statusBadgeNode = statusBadge && <StatusBadge tone="neutral">{statusBadge}</StatusBadge>;

  const priceRow = price && (
    <PriceRow>
      <FromPriceDisplay
        amount={price.amount}
        currencyCode={price.currencyCode}
        label={price.label}
        discount={price.discount}
        locale={price.locale}
        size="base"
      />
    </PriceRow>
  );

  const actions = (customAction || actionButton) && (
    <>
      {customAction}
      {actionButton}
    </>
  );

  return (
    <ItemCardBase {...baseProps}>
      <ImageWrapper
        $aspectRatio={imageAspectRatio}
        $mobileAspectRatio={imageMobileAspectRatio}
        $imageOverlay={imageOverlay}
        $size={size}
      >
        {image.src ? (
          <Image src={image.src} alt={image.alt} sizing={imageSizing} />
        ) : (
          <Placeholder>{image.placeholderLabel ?? 'No image'}</Placeholder>
        )}
        {imageBadge && <ImageBadge>{imageBadge}</ImageBadge>}
        {imageAction && (
          <ImageAction
            type="button"
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              imageAction.onClick();
            }}
          >
            {imageAction.label}
          </ImageAction>
        )}
        {unavailableOverlay}
      </ImageWrapper>

      <Content $textPosition={textPosition} $size={size}>
        {isSmall ? (
          <>
            <Details>
              <Title $bold={boldTitle} $size={size}>
                {title}
              </Title>
              {meta && <Meta $size={size}>{meta}</Meta>}
            </Details>

            {statusBadgeNode}
            {priceRow}

            {actions && <ActionRow>{actions}</ActionRow>}
          </>
        ) : (
          <ContentRow>
            <TextColumn>
              <TitleMetaGroup>
                <Title $bold={boldTitle} $size={size}>
                  {title}
                </Title>
                {meta && <Meta $size={size}>{meta}</Meta>}
              </TitleMetaGroup>

              {statusBadgeNode}
              {priceRow}
            </TextColumn>

            {actions}
          </ContentRow>
        )}
      </Content>
    </ItemCardBase>
  );
}
